import React, {useEffect, useState} from 'react'
import styled from '@emotion/styled'

import {cssTheme} from '@vio/atlas/themes'
import {
  getBrandHeaderLogoUrl,
  getLogoHeight
} from '@vio/core/src/_web/brand/utils'
import {RouterAgnosticLink as Link} from '@vio/core/src/_web/router/components/RouterAgnosticLink'
import {Brand} from '@vio/core/src/brand/types'
import urls from '@vio/core/src/utils/url'

export type BrandLogoComponent = React.FunctionComponent<
  {
    brand: Brand
    compact?: boolean
    withIconText?: boolean
  } & React.ImgHTMLAttributes<HTMLImageElement>
>

interface Props {
  /** Brand configuration. It can be one of our brands or a white-label brand. */
  brand: Brand
  /** Logo component to render. Receives the brand and standard <img> attributes. */
  BrandLogoComponent: BrandLogoComponent
  /** Which url should the logo send the user when clicked upon */
  logoUrl?: string
  /** The alt text for the logo */
  logoAlt?: string
  /** Whether to disable the click action in the logo */
  disableLogoClick?: boolean
  /** Whether to render the compact (icon) variant of the logo */
  compact?: boolean
  /** Whether to render the icon together with its text (compact Vio logo). */
  withIconText?: boolean
}

const StyledLogo = styled.a`
  display: flex;
  align-items: center;

  /* Only show the ring for keyboard nav, not on mouse click (matches the
   * legacy/production logo, which renders no focus ring on click). */
  &:focus-visible {
    outline: 2px solid ${cssTheme.colors.content.interactive.primary};
    border-radius: 2px;
    outline-offset: 8px;
  }
`

const StyledLogoLink = StyledLogo.withComponent(Link)

/**
 * Renders the brand logo wrapped in the appropriate link for the NavBar
 * `startContent`. Replaces the logo logic that the legacy `Header`/`CompactHeader`
 * encapsulated, now that `NavBar` is a pure slot-holder.
 */
export const NavBarBrandLogo = ({
  brand,
  BrandLogoComponent,
  logoUrl,
  logoAlt,
  disableLogoClick,
  compact,
  withIconText
}: Props) => {
  const [brandLogoUrl, setBrandLogoUrl] = useState(logoUrl)

  useEffect(() => {
    // some partners want a custom logo url, so we check for that first
    setBrandLogoUrl(getBrandHeaderLogoUrl(brand, logoUrl))
  }, [brand, logoUrl])

  const logo = (
    <BrandLogoComponent
      brand={brand}
      compact={compact}
      withIconText={withIconText}
      height={getLogoHeight(brand, compact)}
      alt={logoAlt ?? brand.name}
    />
  )

  if (disableLogoClick) {
    return logo
  }

  if (brandLogoUrl) {
    return (
      <StyledLogo data-id="BrandLogo" href={brandLogoUrl}>
        {logo}
      </StyledLogo>
    )
  }

  return (
    <StyledLogoLink data-id="BrandLogo" to={urls.root}>
      {logo}
    </StyledLogoLink>
  )
}
